import { useEffect, useMemo, useState } from 'react';
import { usePersistentState } from '../lib/usePersistentState';
import { useNavigate } from 'react-router-dom';
import { rutaParaResponder } from '../lib/emailThreads';
import { localeUI } from '../lib/format';
import { Bot, FolderOpen, Mail, MessageSquare, MonitorSmartphone, Phone, Timer } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';
import { ClaimEmailBox } from './ClaimEmailBox';
import { EmailBody } from './EmailBody';
import { EmailCard, type EmailLike } from './email/EmailThread';

/**
 * HO COMMUNICATION — todo lo hablado con el homeowner, en orden, en un solo lugar.
 *
 * Los tres canales viven en tres pantallas distintas (Emails, SMS Inbox, Call Monitoring), así que
 * para responder "¿qué le dijimos y cuándo?" había que abrir las tres y ordenar de memoria. Y lo
 * que pasó por teléfono —casi siempre lo importante— era lo que menos se veía.
 *
 * La línea de tiempo va de lo MÁS NUEVO a lo más viejo: se abre para ver qué pasó último, no cómo
 * empezó todo. Cada entrada dice quién de la oficina lo hizo; las llamadas traen su grabación y el
 * resumen de la IA, plegado, porque leer el resumen es mucho más rápido que escuchar 8 minutos.
 *
 * EL CUARTO CANAL ES EL CLIENT PORTAL, y hasta hoy esta pestaña era una promesa incumplida: el aviso
 * que le llega al claim team cuando el asegurado escribe desde el portal apunta acá
 * (`/claims/:id?tab=ho-comm`) y no había ni el mensaje ni forma de contestarlo. Ahora los mensajes del
 * portal entran en la misma línea de tiempo y el cuadro de abajo tiene un tercer modo para responder
 * por ese canal.
 */
type Evento = {
  id: string;
  kind: 'email' | 'sms' | 'call' | 'portal';
  direction: 'in' | 'out';
  at: string;
  actor: string | null;
  subject: string | null;
  body: string | null;
  party: string | null;
  status: string | null;
  /** Solo correos: las dos puntas reales y el seguimiento (entregado/abierto/clic) del envío. */
  from_address?: string | null;
  to_address?: string | null;
  cc_address?: string | null;
  delivered_at?: string | null;
  opened_at?: string | null;
  clicked_at?: string | null;
  duration_sec: number | null;
  recording_id: string | null;
  ai_summary: string | null;
  has_transcript: boolean;
};
type Resp = { events: Evento[]; counts: { email: number; sms: number; call: number; portal: number } };

/** Lo que devuelve la ruta de respuesta: qué pasó con el correo, sin adornar. */
type Respondido = { ok: true; email: 'sent' | 'skipped_dry_run' | 'suppressed' | 'sin_email' | 'failed' };

const META = {
  email: { icon: Mail, color: 'var(--info)', bg: 'var(--info-soft)' },
  sms: { icon: MessageSquare, color: 'var(--green)', bg: 'var(--green-soft)' },
  call: { icon: Phone, color: '#7e22ce', bg: '#f3e8ff' },
  portal: { icon: MonitorSmartphone, color: 'var(--accent)', bg: 'var(--surface-2)' },
} as const;

const dur = (s: number | null) => (s == null ? null : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);

/**
 * Un evento de correo → la forma que entiende la tarjeta compartida (components/email/EmailThread).
 * El timeline trae UNA sola "otra punta" (`party`): en los recibidos es de quién vino, en los
 * enviados a quién fue. Se reparte según la dirección para que la cabecera diga "quién → a quién"
 * igual que en el resto de la app; lo que el timeline no trae (cc, adjuntos, aperturas) queda vacío.
 */
const comoEmail = (e: Evento): EmailLike => ({
  id: e.id,
  direction: e.direction,
  created_at: e.at,
  author: e.actor,
  subject: e.subject,
  body: e.body,
  status: e.status,
  // Las dos puntas y el seguimiento vienen del backend (hoTimeline); `party` queda de respaldo.
  from_address: e.from_address ?? (e.direction === 'in' ? e.party : null),
  to_address: e.to_address ?? (e.direction === 'out' ? e.party : null),
  cc_address: e.cc_address ?? null,
  delivered_at: e.delivered_at ?? null,
  opened_at: e.opened_at ?? null,
  clicked_at: e.clicked_at ?? null,
});

export function HoCommunicationTab({ claimId, phone }: { claimId: string; phone?: string | null }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  /**
   * `portal_enabled` ES LA MISMA LLAVE QUE ESCONDE EL NAV Y LA PESTAÑA DE SETTINGS, y acá hace falta
   * por una razón concreta: el router de `/api/client-portal/*` contesta **404** de entrada cuando
   * `PORTAL_ENABLED` está apagado en Railway. Sin este chequeo, el botón "Responder en el portal"
   * existiría igual y el único aviso sería un 404 crudo — un botón que parece no hacer nada. Los
   * mensajes del portal SÍ se siguen leyendo en la línea de tiempo (ésa no pasa por ese router):
   * lo que se esconde es la punta que escribe, no la historia.
   */
  const { me } = useAuth();
  const portalDisponible = me?.kind === 'tenant' && Boolean(me.user?.portal_enabled);
  const [data, setData] = useState<Resp | null>(null);
  const [filtro, setFiltro] = useState<'all' | 'email' | 'sms' | 'call' | 'portal'>('all');
  const [abierto, setAbierto] = useState<string | null>(null);
  const [modo, setModo] = useState<'email' | 'sms' | 'portal'>('email');
  // Lo que se escribe acá sobrevive al cambio de pestaña (dueño 2026-10-20): borrador por claim.
  const [sms, setSms] = usePersistentState(`hoc:${claimId}:sms`, '');
  const [portal, setPortal] = usePersistentState(`hoc:${claimId}:portal`, '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const { fallo, marcar, limpiar } = useFallo();
  const cargar = () => api.get<Resp>(`/api/claims/${claimId}/ho-communication`).then((d) => { limpiar(); setData(d); }).catch((e) => { marcar(e); setData(null); });
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [claimId]);

  const eventos = useMemo(
    () => (data?.events ?? []).filter((e) => filtro === 'all' || e.kind === filtro),
    [data, filtro],
  );

  async function enviarSms() {
    // Sin teléfono no hay a dónde mandarlo: el backend lo rechazaría igual, pero avisar acá
    // explica POR QUÉ en vez de tirar un "invalid_body".
    if (!sms.trim() || !phone) { if (!phone) setMsg(t('hoc.noPhone')); return; }
    setBusy(true); setMsg(null);
    try {
      await api.post(`/api/claims/${claimId}/sms`, { body: sms.trim(), phone, recipientType: 'homeowner' });
      setSms(''); setMsg(t('hoc.smsSent')); await cargar();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  /**
   * Responder por el CANAL DEL PORTAL. El resultado del correo se dice tal cual vino: en prod el
   * portal está en modo prueba, así que lo normal hoy es `skipped_dry_run` — la respuesta queda
   * guardada y el correo NO sale. Callarlo sería dejar a la oficina creyendo que el cliente ya se
   * enteró.
   */
  async function responderPortal() {
    if (!portal.trim()) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.post<Respondido>(`/api/client-portal/claims/${claimId}/responder`, { text: portal.trim() });
      setPortal('');
      setMsg(t(`hoc.pr.${r.email}`));
      await cargar();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }
  async function llamar() {
    // El chequeo ANTES de setBusy: al revés, salir por acá dejaba los botones deshabilitados
    // para siempre porque el `finally` nunca corría.
    if (!phone) { setMsg(t('hoc.noPhone')); return; }
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/call`, { to: phone, recipientType: 'homeowner' }); setMsg(t('hoc.calling')); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  // Antes esto se quedaba en "Cargando…" para siempre si el GET fallaba: un spinner eterno.
  if (fallo) return <NoCargo onRetry={cargar} />;
  if (!data) return <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: 12 }}>{t('common.loadingDots')}</div>;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <style>{`
        @keyframes hoc-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .hoc-item { animation: hoc-in .28s ease both; }
        @media (prefers-reduced-motion: reduce) { .hoc-item { animation: none; } }
        .hoc-item:hover .hoc-dot { transform: scale(1.15); }
        .hoc-dot { transition: transform .15s ease; }
      `}</style>

      {/* Filtros por canal, con el conteo de cada uno. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {([['all', FolderOpen, t('hoc.all'), data.counts.email + data.counts.sms + data.counts.call + data.counts.portal],
           ['email', META.email.icon, t('hoc.emails'), data.counts.email],
           ['sms', META.sms.icon, t('hoc.sms'), data.counts.sms],
           ['call', META.call.icon, t('hoc.calls'), data.counts.call],
           ['portal', META.portal.icon, t('hoc.portal'), data.counts.portal]] as const).map(([k, Ic, lb, n]) => (
          <button key={k} className={`chipx${filtro === k ? ' active' : ''}`} onClick={() => setFiltro(k)}>
            <Ic size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {lb} <span style={{ opacity: 0.7 }}>{n}</span>
          </button>
        ))}
      </div>

      {/* ── LA LÍNEA DE TIEMPO ── */}
      {eventos.length === 0 ? (
        <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: 14, textAlign: 'center', border: '1px dashed var(--line)', borderRadius: 10 }}>
          {t('hoc.empty')}
        </div>
      ) : (
        <div style={{ position: 'relative', paddingLeft: 26 }}>
          {/* El riel vertical que hace que se lea como una línea y no como una lista suelta. */}
          <span style={{ position: 'absolute', left: 9, top: 6, bottom: 6, width: 2, background: 'var(--line)', borderRadius: 2 }} />
          <div style={{ display: 'grid', gap: 8 }}>
            {eventos.map((e, i) => {
              const m = META[e.kind];
              const entrante = e.direction === 'in';
              const open = abierto === e.id;
              return (
                <div key={e.id} className="hoc-item" style={{ position: 'relative', animationDelay: `${Math.min(i, 12) * 22}ms` }}>
                  <span className="hoc-dot" style={{
                    position: 'absolute', left: -25, top: 9, width: 20, height: 20, borderRadius: '50%',
                    background: m.bg, color: m.color, display: 'grid', placeItems: 'center',
                    border: '2px solid var(--card)', boxShadow: '0 0 0 1.5px var(--line)',
                  }}><m.icon size={11} strokeWidth={1.75} /></span>

                  {e.kind === 'email' ? (
                    /* CORREOS: la MISMA tarjeta que el hilo del claim (fondo por dirección, quién →
                       a quién, chip nuevo/respuesta, seguimiento del enviado). Va directo como
                       contenido del ítem —sin el cuadro genérico de abajo— porque trae su propio
                       marco y encimarle otro dejaba dos bordes. Arranca recogida: acá manda el
                       timeline, no el correo. */
                    <EmailCard message={comoEmail(e)} defaultOpen={false} showSubject onReply={(m) => navigate(rutaParaResponder(claimId, m))} />
                  ) : (
                    <div style={{
                      border: '1px solid var(--line)', borderLeft: `3px solid ${m.color}`, borderRadius: 9,
                      background: 'var(--card)', padding: '8px 10px', display: 'grid', gap: 5,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        {/* Entrante o saliente: es lo primero que hay que saber de cada renglón. */}
                        <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: entrante ? 'var(--green-soft)' : m.bg, color: entrante ? 'var(--green)' : m.color }}>
                          {entrante ? `↙ ${t('hoc.received')}` : `↗ ${t('hoc.sent')}`}
                        </span>
                        {/* El canal, dicho con palabras: el color del punto no alcanza para saber que
                            esto entró por el portal y no por SMS. */}
                        {e.kind === 'portal' && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hoc.portal')}</span>}
                        {e.actor && <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }}>{e.actor}</span>}
                        {!e.actor && entrante && <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }}>{t('hoc.homeowner')}</span>}
                        {e.kind === 'call' && e.duration_sec != null && (
                          <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}><Timer size={10} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {dur(e.duration_sec)}</span>
                        )}
                        {e.kind === 'call' && e.status && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{e.status}</span>}
                        <span style={{ flex: 1 }} />
                        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
                          {new Date(e.at).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>

                      {e.subject && <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)' }}>{e.subject}</div>}

                      {/* El cuerpo se recorta; se despliega con un click. Un timeline donde cada
                          entrada mide media pantalla deja de ser un timeline. */}
                      {e.body && (
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink)', cursor: 'pointer' }} onClick={() => setAbierto(open ? null : e.id)}>
                          {open ? <EmailBody body={e.body} style={{ fontSize: 'var(--fs-xs)' }} />
                            : <span className="muted" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {e.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 140)}
                              </span>}
                        </div>
                      )}

                      {/* LLAMADAS: grabación + resumen de la IA. */}
                      {e.kind === 'call' && (e.recording_id || e.ai_summary) && (
                        <div style={{ display: 'grid', gap: 5, background: 'var(--surface-2)', borderRadius: 7, padding: '6px 8px' }}>
                          {e.ai_summary && (
                            <div style={{ fontSize: 'var(--fs-2xs)', lineHeight: 1.45 }}>
                              <b style={{ fontSize: 'var(--fs-2xs)', color: m.color }}><Bot size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.aiSummary')}</b>
                              <div style={{ marginTop: 2 }}>{e.ai_summary}</div>
                            </div>
                          )}
                          {e.recording_id && (
                            <audio controls preload="none" src={`/api/recordings/${e.recording_id}/url`} style={{ width: '100%', height: 30 }} />
                          )}
                        </div>
                      )}
                      {e.kind === 'call' && !e.recording_id && (
                        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hoc.noRecording')}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── MÓDULO DE ENVÍO: email, SMS o llamada, sin salir de la pestaña ── */}
      <div className="card" style={{ display: 'grid', gap: 8, borderColor: 'var(--accent)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <b style={{ fontSize: 'var(--fs-sm)' }}><Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.reachOut')}</b>
          <span style={{ flex: 1 }} />
          <button className={`chipx${modo === 'email' ? ' active' : ''}`} onClick={() => setModo('email')}><Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.byEmail')}</button>
          <button className={`chipx${modo === 'sms' ? ' active' : ''}`} onClick={() => setModo('sms')}><MessageSquare size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.bySms')}</button>
          {portalDisponible && <button className={`chipx${modo === 'portal' ? ' active' : ''}`} onClick={() => setModo('portal')}><MonitorSmartphone size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.byPortal')}</button>}
          <button className="btn sm" disabled={busy || !phone} title={phone ?? t('hoc.noPhone')}
            style={{ background: '#7e22ce', color: '#fff', borderColor: 'transparent' }} onClick={llamar}>
            <Phone size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.callNow')}
          </button>
        </div>
        {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</div>}
        {modo === 'email' && <ClaimEmailBox claimId={claimId} departmentKey="claims" />}
        {modo === 'sms' && (
          <div style={{ display: 'grid', gap: 6 }}>
            <textarea className="input" rows={3} value={sms} onChange={(ev) => setSms(ev.target.value)} placeholder={t('hoc.smsPh')} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn sm" disabled={busy || !sms.trim() || !phone} style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} onClick={enviarSms}>
                {busy ? '…' : <><MessageSquare size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('common.send')}</>}
              </button>
              {phone && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>→ {phone}</span>}
            </div>
          </div>
        )}
        {/* RESPONDER POR EL PORTAL. La leyenda de arriba no es decorado: el asegurado ve la respuesta
            cuando entra a su portal, y el correo con el texto completo sale SOLO si el portal está
            abierto para su claim. Hoy en prod está en modo prueba, así que lo normal es que no salga. */}
        {modo === 'portal' && portalDisponible && (
          <div style={{ display: 'grid', gap: 6 }}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('hoc.portalAyuda')}</span>
            <textarea className="input" rows={4} value={portal} maxLength={4000}
              onChange={(ev) => setPortal(ev.target.value)} placeholder={t('hoc.portalPh')} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="btn sm" disabled={busy || !portal.trim()}
                style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }} onClick={responderPortal}>
                {busy ? '…' : <><MonitorSmartphone size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hoc.portalSend')}</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
